/**
 * Markdown Message Component
 * Renders markdown content with syntax highlighting
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock } from './CodeBlock';
import { cn } from '@/lib/utils';

interface MarkdownMessageProps {
  content: string;
  className?: string;
}

export const MarkdownMessage: React.FC<MarkdownMessageProps> = ({
  content,
  className
}) => {
  return (
    <div
      className={cn('markdown-content', className)}
      style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Override pre to pass children through - prevents invalid <pre><div></div></pre> nesting
          pre({ children }) {
            return <>{children}</>;
          },
          // Code blocks
          code({ className, children, ...props }: any) {
            const content = String(children).replace(/\n$/, '');
            const langMatch = /language-(\w+)/.exec(className || '');
            const lang = langMatch ? langMatch[1] : '';

            // Detect if content is a URL - render as clickable link instead of code
            const urlPattern = /^https?:\/\/[^\s]+$/i;
            if (!lang && content.split('\n').length === 1 && urlPattern.test(content.trim())) {
              return (
                <a
                  href={content.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline break-all"
                >
                  {content}
                </a>
              );
            }

            // Detect inline code: no language, single line, short content
            const isInline = !lang && content.split('\n').length === 1 && content.length < 50;
            return (
              <CodeBlock
                className={className}
                inline={isInline}
              >
                {content}
              </CodeBlock>
            );
          },
          
          // Links
          a({ node, children, href, ...props }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                {...props}
              >
                {children}
              </a>
            );
          },
          
          // Paragraphs
          p({ node, children, ...props }) {
            return (
              <p className="mb-3 last:mb-0" {...props}>
                {children}
              </p>
            );
          },
          
          // Headings
          h1({ node, children, ...props }) {
            return (
              <h1 className="text-2xl font-bold mb-3 mt-4 first:mt-0" {...props}>
                {children}
              </h1>
            );
          },
          h2({ node, children, ...props }) {
            return (
              <h2 className="text-xl font-bold mb-2 mt-3 first:mt-0" {...props}>
                {children}
              </h2>
            );
          },
          h3({ node, children, ...props }) {
            return (
              <h3 className="text-lg font-semibold mb-2 mt-3 first:mt-0" {...props}>
                {children}
              </h3>
            );
          },
          
          // Lists
          ul({ node, children, ...props }) {
            return (
              <ul className="list-disc list-inside mb-3 space-y-1" {...props}>
                {children}
              </ul>
            );
          },
          ol({ node, children, ...props }) {
            return (
              <ol className="list-decimal list-inside mb-3 space-y-1" {...props}>
                {children}
              </ol>
            );
          },
          li({ node, children, ...props }) {
            return (
              <li className="ml-4" {...props}>
                {children}
              </li>
            );
          },
          
          // Blockquotes
          blockquote({ node, children, ...props }) {
            return (
              <blockquote 
                className="border-l-4 border-primary pl-4 py-2 my-3 italic bg-muted/50" 
                {...props}
              >
                {children}
              </blockquote>
            );
          },
          
          // Tables
          table({ node, children, ...props }) {
            return (
              <div className="overflow-x-auto my-3">
                <table className="min-w-full border-collapse border border-border" {...props}>
                  {children}
                </table>
              </div>
            );
          },
          thead({ node, children, ...props }) {
            return (
              <thead className="bg-muted" {...props}>
                {children}
              </thead>
            );
          },
          th({ node, children, ...props }) {
            return (
              <th className="border border-border px-4 py-2 text-left font-semibold" {...props}>
                {children}
              </th>
            );
          },
          td({ node, children, ...props }) {
            return (
              <td className="border border-border px-4 py-2" {...props}>
                {children}
              </td>
            );
          },
          
          // Horizontal rule
          hr({ node, ...props }) {
            return <hr className="my-4 border-border" {...props} />;
          },
          
          // Strong/Bold
          strong({ node, children, ...props }) {
            return (
              <strong className="font-bold" {...props}>
                {children}
              </strong>
            );
          },
          
          // Emphasis/Italic
          em({ node, children, ...props }) {
            return (
              <em className="italic" {...props}>
                {children}
              </em>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
